import { useState } from 'react';
import { createProfile } from '@ego/api';
import useAuth from '../../hooks/useAuth';
import useHandleValidation from '../../hooks/useHandleValidation';
import styles from './CompleteProfileModal.module.css';

function deriveDefaults(session) {
  const email = session?.user?.email || '';
  const name = session?.user?.name || '';
  const prefix = email.split('@')[0].toLowerCase().replace(/[^a-z0-9_]/g, '');
  const handle = prefix.length >= 3 ? prefix : '';
  return { name, handle };
}

export default function CompleteProfileModal({ onComplete }) {
  const { session } = useAuth();
  const defaults = deriveDefaults(session);

  const [name, setName] = useState(defaults.name);
  const [handle, setHandle] = useState(defaults.handle);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { status: handleStatus, error: handleError } = useHandleValidation(handle);

  const canSubmit = name.trim().length >= 1 && handleStatus === 'available' && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setLoading(true);

    try {
      await createProfile({ name: name.trim(), handle: handle.toLowerCase().trim() });
      onComplete();
    } catch (err) {
      if (err.message === 'HANDLE_TAKEN') {
        setError('Username was just claimed, try another');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={styles.main}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <h1 className={styles.title}>Complete your profile</h1>
        <p className={styles.subtitle}>Choose a display name and username to get started.</p>
        {error && <p className={styles.error}>{error}</p>}
        <label className={styles.label}>
          <span className={styles.requiredMark}>Display name</span>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className={styles.input}
            maxLength={100}
            required
            autoFocus
          />
        </label>
        <label className={styles.label}>
          <span className={styles.requiredMark}>Username</span>
          <div className={styles.handleWrapper}>
            <span className={styles.handlePrefix}>@</span>
            <input
              type="text"
              value={handle}
              onChange={e => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              className={styles.handleInput}
              maxLength={32}
              required
              placeholder="your_handle"
            />
          </div>
          {handleStatus === 'idle' && <span className={styles.handleHint}>3-32 characters, lowercase letters, numbers, underscores</span>}
          {handleError && <span className={styles.handleHint} data-status={handleStatus}>{handleError}</span>}
          {handleStatus === 'checking' && <span className={styles.handleHint} data-status="checking">Checking...</span>}
          {handleStatus === 'available' && <span className={styles.handleHint} data-status="available">Available</span>}
          <span className={styles.handleNote}>Username cannot be changed later</span>
        </label>
        <button type="submit" className={styles.submit} disabled={!canSubmit}>
          {loading ? 'Creating profile...' : 'Continue'}
        </button>
      </form>
    </main>
  );
}
